/**
 * Layout khu vực admin — sidebar navigation + main content.
 *
 * Tính năng:
 * - Menu động theo vai trò (super admin thêm quản lý khách hàng + admin).
 * - Sidebar responsive: overlay mobile, đóng khi đổi route hoặc Escape.
 * - Footer sidebar: link về website công khai và đăng xuất.
 */
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLogoutAndRedirect } from '../hooks/useLogoutAndRedirect';
import { APP_ROUTES } from '../shared/constants/routes';
import { USER_ROLES } from '../shared/constants/users';
import { TEXT } from '../shared/languages';

/** Bộ icon SVG cho sidebar và mobile topbar */
const NavIcons = {
  dashboard: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true">
      <path
        d="M4 10.5L12 4l8 6.5V19a1.5 1.5 0 01-1.5 1.5H5.5A1.5 1.5 0 014 19v-8.5z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <path d="M9.5 20.5V14h5v6.5" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  ),
  properties: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true">
      <path
        d="M4 7.5A2.5 2.5 0 016.5 5H17.5A2.5 2.5 0 0120 7.5v9A2.5 2.5 0 0117.5 19H6.5A2.5 2.5 0 014 16.5v-9z"
        stroke="currentColor"
        strokeWidth="1.75"
      />
      <path d="M8 9h8M8 13h5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  ),
  contacts: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true">
      <path
        d="M4 7h16v10H4V7z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <path d="M4 9l8 5 8-5" stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round" />
    </svg>
  ),
  customers: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true">
      <circle cx="9" cy="8" r="3" stroke="currentColor" strokeWidth="1.75" />
      <circle cx="17" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.75" />
      <path
        d="M3.5 19c0-2.8 2.46-5 5.5-5M13 19c0-2.2 1.79-4 4-4"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  ),
  users: (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true">
      <circle cx="12" cy="8" r="3.25" stroke="currentColor" strokeWidth="1.75" />
      <path
        d="M5.5 19c0-3.038 2.91-5.5 6.5-5.5s6.5 2.462 6.5 5.5"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  ),
  website: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="8.25" stroke="currentColor" strokeWidth="1.75" />
      <path d="M3.5 12h17M12 3.75c2.2 2.467 3.25 5.2 3.25 8.25S14.2 17.783 12 20.25" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  ),
  logout: (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <path d="M10 7V5.5A1.5 1.5 0 0111.5 4h7A1.5 1.5 0 0120 5.5v13a1.5 1.5 0 01-1.5 1.5h-7A1.5 1.5 0 0110 18.5V17" stroke="currentColor" strokeWidth="1.75" />
      <path d="M14 12H4m0 0l3-3M4 12l3 3" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  menu: (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
      <path d="M5 7h14M5 12h14M5 17h14" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  ),
  close: (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
      <path d="M7 7l10 10M17 7L7 17" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  ),
};

/** Link sidebar với icon và trạng thái active */
const SidebarNavLink = ({ to, end = false, icon, label, onNavigate }) => (
  <NavLink
    to={to}
    end={end}
    className={({ isActive }) => `admin-sidebar-link ${isActive ? 'is-active' : ''}`.trim()}
    onClick={onNavigate}
  >
    <span className="admin-sidebar-link-icon">{icon}</span>
    <span className="admin-sidebar-link-label">{label}</span>
  </NavLink>
);

/** Layout shell admin với sidebar và vùng render trang con (Outlet) */
export const AdminLayout = () => {
  const { user, isLoggingOut } = useAuth();
  const location = useLocation();
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const admin = TEXT.admin;
  const sidebar = admin.sidebar;
  const nav = TEXT.nav;
  const common = TEXT.common;

  const closeMobileNav = () => setIsMobileNavOpen(false);
  const handleLogout = useLogoutAndRedirect({ onBeforeLogout: closeMobileNav });

  // Đóng menu mobile khi chuyển route
  useEffect(() => {
    closeMobileNav();
  }, [location.pathname]);

  // Khóa scroll body và lắng nghe Escape khi menu mobile mở
  useEffect(() => {
    if (!isMobileNavOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMobileNav();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isMobileNavOpen]);

  const roleLabel =
    user?.role === USER_ROLES.superAdmin ? sidebar.superAdminRole : sidebar.adminRole;

  /** Menu cơ bản cho mọi admin */
  const menuItems = [
    { to: APP_ROUTES.admin, end: true, icon: NavIcons.dashboard, label: admin.dashboardTitle },
    { to: APP_ROUTES.adminProperties, icon: NavIcons.properties, label: admin.propertiesNav },
    { to: APP_ROUTES.adminContacts, icon: NavIcons.contacts, label: admin.contactsNav },
  ];

  // Super admin: thêm quản lý khách hàng và tài khoản admin
  if (user?.role === USER_ROLES.superAdmin) {
    menuItems.push({
      to: APP_ROUTES.adminManagedUsers,
      end: true,
      icon: NavIcons.customers,
      label: admin.managedUsersNav,
    });
    menuItems.push({
      to: APP_ROUTES.adminUsers,
      icon: NavIcons.users,
      label: admin.usersNav,
    });
  }

  return (
    <div className={`admin-shell ${isMobileNavOpen ? 'is-mobile-nav-open' : ''}`.trim()}>
      <header className="admin-mobile-topbar">
        <button
          className="admin-mobile-menu-toggle"
          type="button"
          aria-expanded={isMobileNavOpen}
          aria-controls="admin-sidebar"
          aria-label={isMobileNavOpen ? sidebar.closeMenu : sidebar.openMenu}
          onClick={() => setIsMobileNavOpen((open) => !open)}
        >
          {isMobileNavOpen ? NavIcons.close : NavIcons.menu}
        </button>
        <div className="admin-mobile-topbar-copy">
          <strong>{common.brandName}</strong>
          <span>{sidebar.tagline}</span>
        </div>
      </header>

      {isMobileNavOpen && (
        <button
          className="admin-sidebar-backdrop"
          type="button"
          aria-label={sidebar.closeMenu}
          onClick={closeMobileNav}
        />
      )}

      <aside
        id="admin-sidebar"
        className={`admin-sidebar ${isMobileNavOpen ? 'is-open' : ''}`.trim()}
      >
        <div className="admin-sidebar-brand">
          <span className="admin-sidebar-brand-mark" aria-hidden="true">
            {common.brandName.charAt(0)}
          </span>
          <div className="admin-sidebar-brand-copy">
            <strong>{common.brandName}</strong>
            <span>{sidebar.tagline}</span>
          </div>
          <button
            className="admin-sidebar-close"
            type="button"
            aria-label={sidebar.closeMenu}
            onClick={closeMobileNav}
          >
            {NavIcons.close}
          </button>
        </div>

        <div className="admin-sidebar-user">
          {user?.avatar ? (
            <img className="admin-sidebar-user-avatar" src={user.avatar} alt="" />
          ) : (
            <span className="admin-sidebar-user-avatar admin-sidebar-user-avatar--fallback" aria-hidden="true">
              {user?.name?.charAt(0)?.toUpperCase() || 'A'}
            </span>
          )}
          <div className="admin-sidebar-user-copy">
            <strong>{user?.name}</strong>
            <span>{roleLabel}</span>
          </div>
        </div>

        <nav className="admin-sidebar-nav" aria-label={sidebar.menuLabel}>
          <span className="admin-sidebar-nav-label">{sidebar.menuLabel}</span>
          <div className="admin-sidebar-nav-list">
            {menuItems.map((item) => (
              <SidebarNavLink key={item.to} {...item} onNavigate={closeMobileNav} />
            ))}
          </div>
        </nav>

        <div className="admin-sidebar-footer">
          <NavLink to={APP_ROUTES.home} className="admin-sidebar-footer-link" onClick={closeMobileNav}>
            <span className="admin-sidebar-link-icon">{NavIcons.website}</span>
            <span>
              <strong>{nav.viewWebsite}</strong>
              <small>{sidebar.viewWebsiteHint}</small>
            </span>
          </NavLink>
          <button
            className="admin-sidebar-logout"
            type="button"
            onClick={handleLogout}
            disabled={isLoggingOut}
          >
            <span className="admin-sidebar-link-icon">{NavIcons.logout}</span>
            {isLoggingOut ? TEXT.auth.logoutSubmitting : nav.logout}
          </button>
        </div>
      </aside>

      <section className="admin-main">
        <Outlet />
      </section>
    </div>
  );
};
